import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, ArrowLeftRight, FileEdit, ScrollText, Headphones, Menu, LogOut, Crown, Coins, Copy, IndianRupee, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/brand";
import { useAuth } from "@/context/AuthContext";

export const PORTALS = {
  admin: { base: "/admin", role: "admin", label: "Admin" },
  super: { base: "/super-admin", role: "super_admin", label: "Super Admin" },
};

const linksFor = (mode) => {
  const b = PORTALS[mode].base;
  const common = [["Users", `${b}/users`, Users], ["UPI Payments", `${b}/payments`, IndianRupee], ["Live Bets", `${b}/live-bets`, Trophy], ["Cricket Predictions", `${b}/cricket`, Trophy], ["Fantasy", `${b}/fantasy`, Trophy], ["Transactions", `${b}/transactions`, ArrowLeftRight],
    ["Content & Config", `${b}/content`, FileEdit], ["Support Tickets", `${b}/tickets`, Headphones], ["Audit Logs", `${b}/audit`, ScrollText]];
  return mode === "super"
    ? [["Admins & Coins", b, Crown], ["Dashboard", `${b}/dashboard`, LayoutDashboard], ...common]
    : [["Dashboard", b, LayoutDashboard], ...common];
};

const Nav = ({ onNav, mode }) => {
  const { logout } = useAuth();
  const nav = useNavigate();
  return (
    <nav className="space-y-1">
      <p className="mb-2 px-3 text-xs uppercase tracking-wider text-cr-muted">{PORTALS[mode].label}</p>
      {linksFor(mode).map(([l, to, I]) => <NavLink key={l} to={to} end onClick={onNav} data-testid={`admin-nav-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}><I className="h-5 w-5" />{l}</NavLink>)}
      <button data-testid="admin-logout-btn" onClick={async () => { await logout(); nav(`${PORTALS[mode].base}/login`); }} className="side-link mt-6 w-full text-cr-danger"><LogOut className="h-5 w-5" />Logout</button>
    </nav>
  );
};

export const useAdminCoins = () => {
  const [me, setMe] = useState(null);
  const load = () => api.get("/admin/me").then((r) => setMe(r.data)).catch(() => {});
  useEffect(() => {
    load();
    window.addEventListener("admin-coins-changed", load);
    return () => window.removeEventListener("admin-coins-changed", load);
  }, []);
  return me;
};

export default function AdminLayout({ mode = "admin" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const p = PORTALS[mode];
  if (user === null) return <div className="grid min-h-screen place-items-center text-cr-muted">Loading…</div>;
  if (!user || user.role !== p.role) return <Navigate to={`${p.base}/login`} replace />;
  return <Shell mode={mode} open={open} setOpen={setOpen} user={user} />;
}

const Shell = ({ mode, open, setOpen, user }) => {
  const me = useAdminCoins();
  const p = PORTALS[mode];
  return (
    <div data-testid={`${mode}-shell`} className="min-h-screen bg-cr-bg">
      <header className="sticky top-0 z-40 flex h-20 items-center gap-4 border-b border-cr-line bg-[#080909]/90 px-4 backdrop-blur-xl lg:px-6">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger aria-label="Open menu" data-testid="admin-mobile-menu" className="lg:hidden"><Menu className="h-6 w-6" /></SheetTrigger>
          <SheetContent side="left" className="w-72 border-cr-line bg-cr-bg p-4 text-cr-ink"><SheetTitle className="sr-only">Menu</SheetTitle><Nav mode={mode} onNav={() => setOpen(false)} /></SheetContent>
        </Sheet>
        <Logo className="h-12" to={p.base} />
        <span data-testid="portal-badge" className="rounded border border-cr-gold/50 px-2 py-0.5 text-xs font-bold uppercase text-cr-gold">{p.label}</span>
        {me?.admin_code && (
          <button data-testid="admin-code-copy" title="Copy registration link" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/register?code=${me.admin_code}`); toast.success("Registration link copied"); }} className="hidden items-center gap-2 rounded-lg border border-cr-gold/40 px-3 py-2 text-xs md:flex">
            <span className="text-cr-muted">Your code</span><b data-testid="admin-code" className="font-mono text-sm text-cr-gold">{me.admin_code}</b><Copy className="h-3.5 w-3.5" />
          </button>
        )}
        <span data-testid="admin-coin-balance" className="ml-auto flex items-center gap-2 rounded-lg border border-cr-line bg-cr-surface px-3 py-2 text-sm font-bold"><Coins className="h-4 w-4 text-cr-gold" />{me ? (me.unlimited_coins ? "Unlimited coins" : `${me.coins.toLocaleString("en-IN")} coins`) : "…"}</span>
        <span data-testid="admin-email" className="hidden text-sm text-cr-muted sm:block">{user.email}</span>
      </header>
      <div className="flex">
        <aside className="sticky top-20 hidden h-[calc(100vh-5rem)] w-60 shrink-0 border-r border-cr-line p-4 lg:block"><Nav mode={mode} /></aside>
        <main className="min-w-0 flex-1 px-4 py-8 sm:px-8"><Outlet /></main>
      </div>
    </div>
  );
};
